/**
 * @file apiService.js (OPTIMIZED)
 * @description Servicio principal de API - GestiÃ³n de vehÃ­culos y estadÃ­sticas
 * @module services/apiService
 * 
 * CAMBIOS OPTIMIZACIÃ“N v3.0:
 * - IntegraciÃ³n con globalVehiculosState para evitar peticiones duplicadas
 * - Hook useVehiculos ahora se suscribe al estado global
 * - ReducciÃ³n de ~95% en queries a base de datos
 * - De 2,160 queries/hora â†’ 120 queries/hora
 * - Polling inteligente que se pausa cuando la pestaÃ±a estÃ¡ inactiva
 * 
 * Funcionalidades principales:
 * - Hook useVehiculos con auto-refresh cada 30 segundos (optimizado)
 * - Sistema de cachÃ© global optimizado
 * - Procesamiento de estadÃ­sticas por marca
 * - Filtros de vehÃ­culos por estatus (incluyendo estatus 2: NoMay)
 * - DetecciÃ³n inteligente de cambios (hash-based)
 * - Hooks adicionales: useMarcas, useEstadisticasGenerales
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from './authService';
import { getUserInfo, filterVehiculosByPermissions, getMarcasPermitidas, getMarcasDisponibles, clearAllAuthData } from './authService';
import { ESTATUS, API_BASE_URL } from '../config/config';
import { globalVehiculosState } from './vehiculosGlobalState';
import { debeContarParaRFID } from '../utils/vehicleHelpers';

// Intervalo de polling optimizado: 30 segundos en lugar de 5
export const POLLING_INTERVAL = 30000; // 30 segundos

/**
 * CACHÃ‰ GLOBAL
 * Evita re-cÃ¡lculos costosos de estadÃ­sticas
 */
let processingCache = new Map();
const PROCESSING_CACHE_TTL = 5000; // 5 segundos

/**
 * Procesa datos de vehÃ­culos y genera estadÃ­sticas por marca
 * Implementa cachÃ© inteligente basado en hash de usuario y cantidad de vehÃ­culos
 * 
 * EstadÃ­sticas generadas por marca:
 * - inventario: vehÃ­culos con estatus 1
 * - noMayoreados: vehÃ­culos con estatus 2 (NoMay)
 * - enTransito: vehÃ­culos con estatus 3
 * - facturadosNoEntregados: vehÃ­culos con estatus 6
 * - lecturaRFID: vehÃ­culos detectados por RFID
 * - faltantesRFID: vehÃ­culos sin detecciÃ³n RFID
 * - enMovimiento: vehÃ­culos en trÃ¡nsito
 * - granTotal: suma de inventario + traspasos + NoMay
 * 
 * @function procesarDatosMarcas
 * @param {Array} vehiculos - Array de vehÃ­culos a procesar
 * @returns {Array} Array de objetos con estadÃ­sticas por marca
 */
const procesarDatosMarcas = (vehiculos) => {
  if (!Array.isArray(vehiculos) || vehiculos.length === 0) {
    return [];
  }

  const userInfo = getUserInfo();
  const userHash = userInfo ? `${userInfo.username}-${userInfo.rol}` : 'anonymous';
  const dataHash = `${userHash}-${vehiculos.length}`;
  const cached = processingCache.get(dataHash);
  const now = Date.now();

  // Retornar cache si es vÃ¡lido
  if (cached && (now - cached.timestamp) < PROCESSING_CACHE_TTL) {
    return cached.result;
  }

  // Filtrar por permisos
  const vehiculosFiltrados = filterVehiculosByPermissions(vehiculos);

  const marcasStats = {};

  // Filtrar solo vehÃ­culos con estatus vÃ¡lidos (1, 2, 3, 6)
  // y solo marcas registradas en el catÃ¡logo real (evita mostrar marcas
  // que el extractor trae pero que no existen en la tabla Marca)
  const marcasCatalogo = new Set(getMarcasDisponibles());
  const vehiculosValidos = vehiculosFiltrados.filter(v =>
    [ESTATUS.INVENTARIO, ESTATUS.NO_MAYOREADO, ESTATUS.EN_TRANSITO, ESTATUS.TRASPASO_SIN_RECIBIR, ESTATUS.FACTURADO_NO_ENTREGADO].includes(v.estatus) &&
    (marcasCatalogo.size === 0 || marcasCatalogo.has((v.Marca || '').toUpperCase()))
  );
  // Procesar cada vehÃ­culo
  vehiculosValidos.forEach((vehiculo) => {
    const marca = vehiculo.Marca;

    if (!marca) return;

    // Inicializar estadÃ­sticas de marca
    if (!marcasStats[marca]) {
      marcasStats[marca] = {
        marca: marca,
        inventario: 0,
        noMayoreados: 0,
        enTransito: 0,
        traspasoSinRecibir: 0,
        facturadosNoEntregados: 0,
        lecturaRFID: 0,
        faltantesRFID: 0,
        enMovimiento: 0,
        enMovimientoInv: 0,
        enReposo: 0,
        totalVehiculos: 0,
        totalFisicoEsperado: 0,
        granTotal: 0,
        porcentajeInventario: 0,
        totalAlertas: 0,
        tieneAlertas: false
      };
    }

    const marcaData = marcasStats[marca];
    marcaData.totalVehiculos++;

    const estatus = vehiculo.estatus;
    const enMov = vehiculo.en_mov;
    const enMov_inv = vehiculo.en_mov_inv;

    // Contadores de movimiento
    if (enMov === 1) marcaData.enMovimiento++;
    else marcaData.enReposo++;

    if (enMov_inv === 1) marcaData.enMovimientoInv++;

    // Contadores por estatus
    switch (estatus) {
      case 1: marcaData.inventario++; break;
      case 2: marcaData.noMayoreados++; break;
      case 3: marcaData.enTransito++; break;
      case 5: marcaData.traspasoSinRecibir++; break;
      case 6: marcaData.facturadosNoEntregados++; break;
    }

    // DetecciÃ³n RFID â€” usa la misma regla centralizada que Dashboard.js
    if (debeContarParaRFID(vehiculo)) {
      if (vehiculo.detectado_rfid === true) {
        marcaData.lecturaRFID++;
      } else {
        marcaData.faltantesRFID++;
      }
    }
  });

  // Calcular totales y porcentajes
  const result = Object.values(marcasStats).map(marcaData => ({
    ...marcaData,
    granTotal: marcaData.inventario + marcaData.enTransito + marcaData.traspasoSinRecibir + marcaData.noMayoreados,
    totalFisicoEsperado: marcaData.inventario + marcaData.facturadosNoEntregados,
    totalAlertas: marcaData.faltantesRFID + marcaData.noMayoreados,
    tieneAlertas: (marcaData.faltantesRFID + marcaData.noMayoreados) > 0,
    porcentajeInventario: (marcaData.lecturaRFID + marcaData.faltantesRFID) > 0
      ? Math.round((marcaData.lecturaRFID / (marcaData.lecturaRFID + marcaData.faltantesRFID)) * 100)
      : 0
  }));

  // Guardar en cache
  processingCache.set(dataHash, {
    result,
    timestamp: now
  });

  // Limpiar cache viejo (mantener mÃ¡ximo 10 entradas)
  if (processingCache.size > 10) {
    const keys = Array.from(processingCache.keys());
    keys.slice(0, 5).forEach(key => processingCache.delete(key));
  }

  return result;
};

/**
 * Factory function que crea funciones de filtrado con cachÃ© incorporado
 * Reduce cÃ³digo duplicado y optimiza el rendimiento
 * 
 * @function createFilterFunction
 * @param {Function} filterFn - FunciÃ³n de filtrado a aplicar
 * @returns {Function} FunciÃ³n de filtrado con cachÃ©
 */
const createFilterFunction = (filterFn) => {
  const cache = new Map();

  return (vehiculos, skipPermissionsFilter = false) => {
    if (!Array.isArray(vehiculos) || vehiculos.length === 0) return [];

    const cacheKey = `${vehiculos.length}-${skipPermissionsFilter}`;
    const cached = cache.get(cacheKey);
    const now = Date.now();

    // Retornar cache si es vÃ¡lido
    if (cached && (now - cached.timestamp) < 5000) {
      return cached.result;
    }

    const vehiculosFiltrados = skipPermissionsFilter ? vehiculos : filterVehiculosByPermissions(vehiculos);
    const result = filterFn(vehiculosFiltrados);

    cache.set(cacheKey, { result, timestamp: now });

    // Limpiar cache viejo
    if (cache.size > 20) {
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }

    return result;
  };
};

/**
 * FUNCIONES DE FILTRADO
 * Todas implementan cachÃ© automÃ¡tico vÃ­a createFilterFunction
 */

/**
 * Filtra vehÃ­culos por estatus especÃ­fico
 * @param {Array} vehiculos - Array de vehÃ­culos
 * @returns {Function} FunciÃ³n que acepta estatus y retorna vehÃ­culos filtrados
 */
export const getVehiculosByEstatus = createFilterFunction(
  (vehiculos) => (estatus) => vehiculos.filter(vehiculo => vehiculo.estatus === estatus)
);

/**
 * Obtiene vehÃ­culos en inventario (estatus 1)
 */
export const getVehiculosInventario = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => vehiculo.estatus === ESTATUS.INVENTARIO)
);

/**
 * Obtiene vehÃ­culos no mayoreados (estatus 2)
 */
export const getVehiculosNoMayoreados = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => vehiculo.estatus === ESTATUS.NO_MAYOREADO)
);

/**
 * Obtiene vehÃ­culos en traspaso sin recibir (estatus 3)
 */
export const getVehiculosEnTransito = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => vehiculo.estatus === ESTATUS.EN_TRANSITO)
);

/**
 * Obtiene vehÃ­culos con traspaso sin recibir (estatus 5)
 */
export const getVehiculosTraspasoSinRecibir = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => vehiculo.estatus === ESTATUS.TRASPASO_SIN_RECIBIR)
);

/**
 * Obtiene vehÃ­culos facturados no entregados (estatus 6)
 */
export const getVehiculosFacturadosNoEntregados = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => vehiculo.estatus === ESTATUS.FACTURADO_NO_ENTREGADO)
);

const ESTATUS_VALIDOS = [ESTATUS.INVENTARIO, ESTATUS.NO_MAYOREADO, ESTATUS.EN_TRANSITO, ESTATUS.TRASPASO_SIN_RECIBIR, ESTATUS.FACTURADO_NO_ENTREGADO];

/**
 * Obtiene solo vehÃ­culos con estatus vÃ¡lidos (1, 2, 3, 5, 6)
 */
export const getVehiculosValidos = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => ESTATUS_VALIDOS.includes(vehiculo.estatus))
);

/**
 * Obtiene vehÃ­culos con detecciÃ³n RFID
 */
export const getVehiculosConRFID = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => ESTATUS_VALIDOS.includes(vehiculo.estatus) && vehiculo.detectado_rfid === true)
);

/**
 * Obtiene vehÃ­culos sin detecciÃ³n RFID (faltantes)
 */
export const getVehiculosSinRFID = createFilterFunction(
  (vehiculos) => vehiculos.filter(vehiculo => ESTATUS_VALIDOS.includes(vehiculo.estatus) && vehiculo.detectado_rfid !== true)
);

/**
 * FunciÃ³n retrocompatible - alias de getVehiculosSinRFID
 */
export const getVehiculosFaltantes = getVehiculosSinRFID;

/**
 * FUNCIONES HELPER
 * Utilidades para mapear cÃ³digos de estatus a nombres y colores
 */

/**
 * Obtiene el nombre legible de un estatus
 * @param {number} estatus - CÃ³digo de estatus (1, 2, 3, 6)
 * @returns {string} Nombre del estatus
 */
export const getStatusName = (estatus) => {
  const statusMap = {
    1: 'Inventario',
    2: 'No Mayoreado',
    3: 'Unidades por arribar',
    6: 'Facturado no entregado',
    7: 'Unidades en Salida'
  };
  return statusMap[estatus] || 'Desconocido';
};

/**
 * Obtiene la clase de color de texto para un estatus
 * @param {number} estatus - CÃ³digo de estatus
 * @returns {string} Clase de Tailwind para color de texto
 */
export const getStatusColor = (estatus) => {
  const colorMap = {
    1: 'text-blue-600',
    2: 'text-orange-600',
    3: 'text-yellow-600',
    6: 'text-pink-600',
    7: 'text-white'
  };
  return colorMap[estatus] || 'text-gray-600';
};

/**
 * Obtiene las clases de color de fondo para un estatus
 * @param {number} estatus - CÃ³digo de estatus
 * @returns {string} Clases de Tailwind para fondo y borde
 */
export const getStatusBgColor = (estatus) => {
  const colorMap = {
    1: 'bg-blue-100 border-blue-200',
    2: 'bg-orange-100 border-orange-200',
    3: 'bg-yellow-100 border-yellow-200',
    6: 'bg-pink-100 border-pink-200',
    7: 'bg-red-800 border-red-800'
  };
  return colorMap[estatus] || 'bg-gray-100 border-gray-200';
};

/**
 * SERVICIO DE VEHÃCULOS (OPTIMIZADO)
 * Gestiona la obtenciÃ³n y cachÃ© de vehÃ­culos desde la API
 * NOTA: Ahora actÃºa como wrapper del globalVehiculosState
 */
export const vehiculosService = {
  /**
   * CachÃ© del servicio (ahora apunta al estado global)
   */
  cache: {
    get data() { return globalVehiculosState.vehiculos; },
    get marcasData() { return globalVehiculosState.marcasData; },
    get timestamp() { return globalVehiculosState.timestamp; },
    get hash() { return globalVehiculosState.hash; },
    get isLoading() { return globalVehiculosState.isLoading; }
  },

  CACHE_TTL: 5 * 1000, // 5 segundos

  /**
   * Obtiene vehÃ­culos desde la API con sistema de cachÃ©
   * OPTIMIZADO: Ahora usa el estado global
   * 
   * @async
   * @param {boolean} forceRefresh - Si es true, ignora el cachÃ©
   * @returns {Promise<Object>} Objeto con vehiculos y marcasData
   * @property {Array} vehiculos - Array de vehÃ­culos
   * @property {Array} marcasData - EstadÃ­sticas procesadas por marca
   */
  obtenerVehiculos: async (forceRefresh = false) => {
    try {
      // Configurar timeout de 20 segundos
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      // NOTA: /api/comparativo/ devuelve ~1.6MB. El adapter XHR de axios (y
      // tambien su adapter 'fetch') se cuelga con esta respuesta y siempre
      // termina abortado por el timeout de arriba, aunque el server ya
      // respondio en <1s - confirmado comparando XHR/axios vs fetch() nativo
      // contra el mismo endpoint, mismos headers, mismo token. Por eso esta
      // llamada puntual usa fetch() nativo en vez de la instancia de axios.
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/api/comparativo/`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (res.status === 401) {
        clearAllAuthData();
        window.dispatchEvent(new CustomEvent('authError', { detail: 'No autorizado' }));
        throw new Error('No autorizado');
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      const vehiculos = Array.isArray(data) ? data : [];
      const marcasData = procesarDatosMarcas(vehiculos);

      return { vehiculos, marcasData };

    } catch (error) {
      // Si falla, intentar usar cachÃ© viejo del estado global
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        throw error;
      }

      if (globalVehiculosState.vehiculos.length > 0) {
        console.warn('Error en API, usando cache del estado global:', error.message);
        return {
          vehiculos: globalVehiculosState.vehiculos,
          marcasData: globalVehiculosState.marcasData
        };
      }

      console.error('Error sin cache disponible:', error);
      throw error;
    }
  },

  /**
   * Obtiene solo las estadÃ­sticas de marcas
   * @async
   * @param {boolean} forceRefresh - Forzar actualizaciÃ³n
   * @returns {Promise<Array>} Array de estadÃ­sticas por marca
   */
  obtenerMarcas: async (forceRefresh = false) => {
    const result = await vehiculosService.obtenerVehiculos(forceRefresh);
    return result.marcasData;
  },

  /**
   * Obtiene estadÃ­sticas generales del sistema
   * @async
   * @param {boolean} forceRefresh - Forzar actualizaciÃ³n
   * @returns {Promise<Object>} Objeto con estadÃ­sticas generales
   */
  obtenerEstadisticasGenerales: async (forceRefresh = false) => {
    const result = await vehiculosService.obtenerVehiculos(forceRefresh);
    const vehiculosValidos = getVehiculosValidos(result.vehiculos);

    return {
      totalVehiculos: vehiculosValidos.length,
      inventario: getVehiculosInventario(result.vehiculos).length,
      noMayoreados: getVehiculosNoMayoreados(result.vehiculos).length,
      enTransito: getVehiculosEnTransito(result.vehiculos).length,
      facturadosNoEntregados: getVehiculosFacturadosNoEntregados(result.vehiculos).length,
      conRFID: getVehiculosConRFID(result.vehiculos).length,
      sinRFID: getVehiculosSinRFID(result.vehiculos).length,
      porcentajeRFID: vehiculosValidos.length > 0
        ? Math.round((getVehiculosConRFID(result.vehiculos).length / vehiculosValidos.length) * 100)
        : 0
    };
  }
};

/**
 * HOOK PRINCIPAL: useVehiculos (OPTIMIZADO)
 * 
 * Hook personalizado que gestiona la carga y actualizaciÃ³n automÃ¡tica de vehÃ­culos
 * 
 * OPTIMIZACIÃ“N v3.0:
 * - Ahora se suscribe al globalVehiculosState en lugar de hacer peticiones directas
 * - MÃºltiples componentes comparten la misma fuente de datos
 * - Polling centralizado reduce queries de DB en ~95%
 * 
 * CaracterÃ­sticas:
 * - Auto-refresh configurable (default: 30 segundos)
 * - DetecciÃ³n inteligente de cambios mediante hash
 * - PrevenciÃ³n de peticiones concurrentes
 * - Sistema de eventos para notificar cambios
 * - GestiÃ³n automÃ¡tica de cleanup
 * - Filtrado por permisos de usuario
 * 
 * @hook
 * @param {Object} config - ConfiguraciÃ³n del hook
 * @param {boolean} [config.autoRefresh=true] - Habilitar actualizaciÃ³n automÃ¡tica
 * @param {number} [config.intervaloActualizacion=30000] - Intervalo en ms (default: 30 segundos)
 * 
 * @returns {Object} Estado y funciones del hook
 * @property {Array} vehiculos - Array completo de vehÃ­culos
 * @property {Array} vehiculosPermitidos - VehÃ­culos filtrados por permisos
 * @property {Array} vehiculosValidos - VehÃ­culos con estatus vÃ¡lidos (1,2,3,6)
 * @property {Array} marcasData - EstadÃ­sticas por marca
 * @property {boolean} loading - true durante carga inicial
 * @property {string|null} error - Mensaje de error si hay fallo
 * @property {boolean} isUpdating - true durante actualizaciÃ³n en background
 * @property {boolean} hasChanges - true si hubo cambios en Ãºltima actualizaciÃ³n
 * @property {Date|null} lastUpdateTime - Timestamp de Ãºltima actualizaciÃ³n exitosa
 * @property {Function} recargar - FunciÃ³n para forzar actualizaciÃ³n manual
 * @property {Object} userInfo - InformaciÃ³n del usuario autenticado
 * @property {Array} marcasPermitidas - Marcas que el usuario puede ver
 */
export const useVehiculos = ({
  autoRefresh = true,
  intervaloActualizacion = POLLING_INTERVAL
} = {}) => {
  // Estado del hook
  const [vehiculos, setVehiculos] = useState([]);
  const [marcasData, setMarcasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  // Referencias para control de estado
  const mountedRef = useRef(true);

  /**
   * Effect principal: SuscripciÃ³n al estado global
   */
  useEffect(() => {
    mountedRef.current = true;


    // Suscribirse al estado global
    const unsubscribe = globalVehiculosState.subscribe((state) => {
      if (!mountedRef.current) return;

      setVehiculos(state.vehiculos);
      setMarcasData(state.marcasData);
      setLoading(state.loading);
      setError(state.error);
      setIsUpdating(state.isUpdating);
      setHasChanges(state.hasChanges);

      // CRÃTICO: Siempre crear nuevo Date para forzar re-render
      if (state.lastUpdateTime) {
        const newDate = new Date(state.lastUpdateTime);
        setLastUpdateTime(newDate);
      } else {
        setLastUpdateTime(null);
      }
    });

    // Iniciar polling si estÃ¡ habilitado - solo si nadie mas lo activo ya.
    // Cada componente que monte useVehiculos() llega aqui; sin este guard,
    // startPolling() dispara su propio fetch(true) (forzado, sin dedup) por
    // cada uno, mandando varias peticiones de ~1.6MB en paralelo al montar
    // el dashboard (Dashboard + Sidebar + SidebarPopupContainer a la vez).
    if (autoRefresh && !globalVehiculosState.isPollingActive) {
      globalVehiculosState.startPolling(intervaloActualizacion);
    }

    // Cleanup
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [autoRefresh, intervaloActualizacion]);

  /**
   * FunciÃ³n pÃºblica para recargar manualmente
   */
  const recargar = useCallback(() => {
    globalVehiculosState.reload();
  }, []);

  // Valores memoizados para optimizaciÃ³n
  const vehiculosPermitidos = useMemo(() => {
    return filterVehiculosByPermissions(vehiculos);
  }, [vehiculos]);

  const vehiculosValidos = useMemo(() => {
    if (!vehiculosPermitidos || vehiculosPermitidos.length === 0) return [];
    return vehiculosPermitidos.filter(v => [...ESTATUS_VALIDOS, ESTATUS.ENTREGADO_STANDBY].includes(v.estatus));
  }, [vehiculosPermitidos]);

  const userInfo = useMemo(() => getUserInfo(), []);
  const marcasPermitidas = useMemo(() => getMarcasPermitidas(), []);

  return {
    vehiculos,
    vehiculosPermitidos,
    vehiculosValidos,
    marcasData,
    loading,
    error,
    isUpdating,
    hasChanges,
    lastUpdateTime,
    recargar,
    userInfo,
    marcasPermitidas
  };
};

/**
 * HOOK: useMarcas (OPTIMIZADO)
 * Hook simplificado para obtener solo estadÃ­sticas de marcas
 * 
 * @hook
 * @param {Object} config
 * @param {number} [config.intervaloActualizacion=30000] - Intervalo de actualizaciÃ³n
 * @returns {Object} Estado del hook
 * @property {Array} marcasData - EstadÃ­sticas por marca
 * @property {boolean} loading - Estado de carga
 * @property {string|null} error - Mensaje de error
 * @property {Function} recargar - FunciÃ³n de recarga manual
 */
export const useMarcas = ({ intervaloActualizacion = POLLING_INTERVAL } = {}) => {
  const [marcasData, setMarcasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Suscribirse al estado global
    const unsubscribe = globalVehiculosState.subscribe((state) => {
      if (!mountedRef.current) return;

      setMarcasData(state.marcasData);
      setLoading(state.loading);
      setError(state.error);
    });

    // Iniciar polling - solo si nadie mas lo activo ya (ver guard en useVehiculos)
    if (!globalVehiculosState.isPollingActive) {
      globalVehiculosState.startPolling(intervaloActualizacion);
    }

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [intervaloActualizacion]);

  const recargar = useCallback(() => {
    globalVehiculosState.reload();
  }, []);

  return {
    marcasData: marcasData || [],
    loading,
    error,
    recargar
  };
};

/**
 * HOOK: useEstadisticasGenerales (OPTIMIZADO)
 * Hook para obtener estadÃ­sticas agregadas del sistema
 * 
 * @hook
 * @param {Object} config
 * @param {number} [config.intervaloActualizacion=30000] - Intervalo de actualizaciÃ³n
 * @returns {Object} Estado del hook
 * @property {Object|null} estadisticas - EstadÃ­sticas generales
 * @property {boolean} loading - Estado de carga
 * @property {string|null} error - Mensaje de error
 * @property {Function} recargar - FunciÃ³n de recarga manual
 */
export const useEstadisticasGenerales = ({ intervaloActualizacion = POLLING_INTERVAL } = {}) => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Suscribirse al estado global
    const unsubscribe = globalVehiculosState.subscribe((state) => {
      if (!mountedRef.current) return;

      if (state.vehiculos.length > 0) {
        const vehiculosValidos = getVehiculosValidos(state.vehiculos);

        const stats = {
          totalVehiculos: vehiculosValidos.length,
          inventario: getVehiculosInventario(state.vehiculos).length,
          noMayoreados: getVehiculosNoMayoreados(state.vehiculos).length,
          enTransito: getVehiculosEnTransito(state.vehiculos).length,
          facturadosNoEntregados: getVehiculosFacturadosNoEntregados(state.vehiculos).length,
          conRFID: getVehiculosConRFID(state.vehiculos).length,
          sinRFID: getVehiculosSinRFID(state.vehiculos).length,
          porcentajeRFID: vehiculosValidos.length > 0
            ? Math.round((getVehiculosConRFID(state.vehiculos).length / vehiculosValidos.length) * 100)
            : 0
        };

        setEstadisticas(stats);
      }

      setLoading(state.loading);
      setError(state.error);
    });

    // Iniciar polling - solo si nadie mas lo activo ya (ver guard en useVehiculos)
    if (!globalVehiculosState.isPollingActive) {
      globalVehiculosState.startPolling(intervaloActualizacion);
    }

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [intervaloActualizacion]);

  const recargar = useCallback(() => {
    globalVehiculosState.reload();
  }, []);

  return {
    estadisticas,
    loading,
    error,
    recargar
  };
};